/**
 * Cuts per-utterance clips out of the agent's `user_utterances` asset: one mono
 * 8 kHz µ-law WAV whose events carry byte offsets into its data chunk. Clips are
 * decoded to 16-bit PCM so every browser can play them.
 */

const SAMPLE_RATE = 8000;

export interface WavDataChunk {
  offset: number;
  length: number;
}

/** Locates the `data` chunk by walking the RIFF chunks (odd-sized chunks are padded). */
export function findDataChunk(buffer: ArrayBuffer): WavDataChunk | null {
  const view = new DataView(buffer);
  if (buffer.byteLength < 12) return null;
  const tag = (at: number) => String.fromCharCode(...new Uint8Array(buffer, at, 4));
  if (tag(0) !== 'RIFF' || tag(8) !== 'WAVE') return null;

  let pos = 12;
  while (pos + 8 <= buffer.byteLength) {
    const id = tag(pos);
    const size = view.getUint32(pos + 4, true);
    const body = pos + 8;
    if (id === 'data') {
      return { offset: body, length: Math.min(size, buffer.byteLength - body) };
    }
    pos = body + size + (size % 2);
  }
  return null;
}

/** G.711 µ-law byte to a 16-bit linear sample. */
export function mulawToLinear(byte: number): number {
  const u = ~byte & 0xff;
  const sign = u & 0x80;
  const exponent = (u >> 4) & 0x07;
  const mantissa = u & 0x0f;
  const magnitude = (((mantissa << 3) + 0x84) << exponent) - 0x84;
  return sign ? 0 - magnitude : magnitude;
}

/** Builds a playable 16-bit PCM WAV from µ-law samples. */
export function mulawToPcmWav(samples: Uint8Array): ArrayBuffer {
  const dataBytes = samples.length * 2;
  const buffer = new ArrayBuffer(44 + dataBytes);
  const view = new DataView(buffer);
  const write = (at: number, s: string) => {
    for (let i = 0; i < s.length; i++) view.setUint8(at + i, s.charCodeAt(i));
  };

  write(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  write(8, 'WAVE');
  write(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, SAMPLE_RATE, true);
  view.setUint32(28, SAMPLE_RATE * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  write(36, 'data');
  view.setUint32(40, dataBytes, true);
  for (let i = 0; i < samples.length; i++) {
    view.setInt16(44 + i * 2, mulawToLinear(samples[i]), true);
  }
  return buffer;
}

/** The clip at `offset`/`length` bytes into the data chunk as a PCM WAV, or null when out of range. */
export function sliceUtterance(
  buffer: ArrayBuffer,
  chunk: WavDataChunk,
  offset: number,
  length: number,
): ArrayBuffer | null {
  if (offset < 0 || length <= 0 || offset >= chunk.length) return null;
  const end = Math.min(offset + length, chunk.length);
  return mulawToPcmWav(new Uint8Array(buffer, chunk.offset + offset, end - offset));
}
